import type { Component } from "vue"
import { BadgeCheck, Bot, Film, Image, Palette, Sparkles } from "lucide-vue-next"

// What each bit of a plan's `functions` mask (proto/subscriptions.proto `Plan.functions`) unlocks -
// mirrors the backend's `[Flags] enum SubscriptionFunctions : ulong`. The backend only sends the
// mask, so the comparison table's feature rows come from here. A bit set on some plan but missing
// here (a feature added server-side first) still shows up, under a generic label and icon.
/** `short`: the name inside a sentence ("Всё из Classic, плюс бейдж и цвет ника"). */
export const SUBSCRIPTION_FEATURES: Record<number, { label: string; short: string; icon?: Component }> = {
  0: { label: "Анимированный аватар (GIF)", short: "GIF-аватар", icon: Film }, // AnimatedAvatar
  1: { label: "Свой баннер профиля", short: "баннер", icon: Image }, // CustomProfileBanner
  2: { label: "Уникальный бейдж рядом с ником", short: "бейдж", icon: BadgeCheck }, // ProfileBadge
  3: { label: "Расширенная цветовая тема ника", short: "цвет ника", icon: Palette }, // CustomNameColor
  4: { label: "ИИ-агенты", short: "ИИ-агенты", icon: Bot }, // AiAgents
}

export interface SubscriptionFeatureRow {
  bit: number
  label: string
  short: string
  icon: Component
}

function toRow(bit: number): SubscriptionFeatureRow {
  const known = SUBSCRIPTION_FEATURES[bit]
  const label = known?.label ?? `Функция ${bit + 1}`
  return { bit, label, short: known?.short ?? label.toLowerCase(), icon: known?.icon ?? Sparkles }
}

/** One row per bit set on any of the given masks, lowest bit first. */
export function featureRows(masks: number[]): SubscriptionFeatureRow[] {
  const all = masks.reduce((acc, mask) => acc | BigInt(Math.trunc(mask)), 0n)
  const rows: SubscriptionFeatureRow[] = []
  for (let bit = 0; bit < 63; bit++) {
    if ((all >> BigInt(bit)) & 1n) rows.push(toRow(bit))
  }
  return rows
}

export function hasFeature(mask: number, bit: number) {
  return ((BigInt(Math.trunc(mask)) >> BigInt(bit)) & 1n) === 1n
}

/** `SUBSCRIPTION_FEATURES` bit of the badge shown next to a subscriber's name. */
export const PROFILE_BADGE_BIT = 2

/**
 * Display name of the tier ("Premium") whose badge this user's subscription shows, or `null` when
 * there is none: no plan (`tier_code: "none"`), the plan doesn't include the badge, or it expired.
 * Works on anyone's `common.Subscription` (message authors, group members), not just our own.
 */
export function subscriptionBadgeTier(subscription?: { tier_code: string; functions: number; expires_at?: string | null } | null): string | null {
  if (!subscription || !subscription.tier_code || subscription.tier_code === "none") return null
  if (!hasFeature(subscription.functions, PROFILE_BADGE_BIT)) return null
  if (subscription.expires_at) {
    const expires = new Date(subscription.expires_at).getTime()
    if (!Number.isNaN(expires) && expires <= Date.now()) return null
  }
  return subscription.tier_code.charAt(0).toUpperCase() + subscription.tier_code.slice(1)
}
